import {
	CheckIcon,
	XMarkIcon,
	QuestionMarkCircleIcon,
	HomeModernIcon,
	HomeIcon,
	BuildingOffice2Icon,
	ChevronRightIcon,
	ChevronLeftIcon,
} from '@heroicons/react/24/outline';
import { useState } from 'react';
import client from '../api/client';
import { useAlert } from '../context/AlertContext';
import BillDropzone from './Global/BillDropzone';

// --- Wizard step definitions ------------------------------------------------
// Each step has a key, title, type, and (for choices) options. `required`
// steps block Continue until answered and hide the Skip button.

const PROPERTY_TYPES = [
	{ value: 'Single-Family', icon: HomeModernIcon },
	{ value: 'Townhouse', icon: HomeIcon },
	{ value: 'Manufactured', icon: BuildingOffice2Icon },
];

const FINANCING = [
	{ value: 'Lease/PPA', sub: 'Save 20–30% on utility bills' },
	{ value: 'Purchase', sub: 'Long-term ROI' },
	{ value: "I'm Not Sure", sub: "I'd like help deciding" },
];

const YES_NO = [
	{ value: 'Yes', icon: CheckIcon },
	{ value: 'No', icon: XMarkIcon },
];
const YES_NO_MAYBE = [
	{ value: 'Yes', icon: CheckIcon },
	{ value: 'No', icon: XMarkIcon },
	{ value: 'Not Sure', icon: QuestionMarkCircleIcon },
];

const STEPS = [
	{
		key: 'zip',
		type: 'zip',
		title: 'Where is your property located?',
		subtitle: 'Enter 5-Digit Zip Code',
		required: true,
	},
	{
		key: 'owns',
		type: 'choice',
		title: 'Do you own your property?',
		subtitle: 'Select One',
		options: YES_NO,
		required: true,
		columns: 2,
	},
	{
		key: 'propertyType',
		type: 'choice',
		title: 'What type of property do you own?',
		options: PROPERTY_TYPES,
		columns: 2,
	},
	{
		key: 'financing',
		type: 'choice',
		title: 'Are you interested in leasing or purchasing your solar system?',
		subtitle: 'Select one.',
		options: FINANCING,
		columns: 2,
	},
	{
		key: 'battery',
		type: 'choice',
		title: 'Would you like a battery backup with your installation?',
		subtitle: 'Select One',
		options: YES_NO_MAYBE,
		columns: 3,
	},
	{
		key: 'contact',
		type: 'contact',
		title: 'How can we reach you?',
	},
];

export default function Contact() {
	const { setAlert } = useAlert();
	// Two ways in: the guided ZIP quiz, or a plain "just send a message" form.
	const [mode, setMode] = useState('guided'); // 'guided' | 'message'
	const [step, setStep] = useState(0);
	const [answers, setAnswers] = useState({});
	const [contact, setContact] = useState({
		firstName: '',
		lastName: '',
		email: '',
		phone: '',
	});
	const [billFile, setBillFile] = useState(null);
	const [submitting, setSubmitting] = useState(false);
	const [done, setDone] = useState(false);

	const current = STEPS[step];
	const progress = ((step + 1) / STEPS.length) * 100;

	const setAnswer = (key, value) => setAnswers((a) => ({ ...a, [key]: value }));

	// Can we advance from the current step?
	const canContinue = () => {
		if (current.type === 'zip') return /^\d{5}$/.test(answers.zip || '');
		if (current.required) return !!answers[current.key];
		return true;
	};

	const next = () => setStep((s) => Math.min(s + 1, STEPS.length - 1));
	const back = () => setStep((s) => Math.max(s - 1, 0));
	const skip = () => next();

	const submit = async () => {
		if (!contact.firstName || !contact.email || !contact.phone) {
			return setAlert('error', 'Please fill out name, email, and phone.');
		}
		setSubmitting(true);

		// Fold the wizard answers into the message body; zip -> address.
		const lines = [
			answers.owns && `Owns property: ${answers.owns}`,
			answers.propertyType && `Property type: ${answers.propertyType}`,
			answers.financing && `Interested in: ${answers.financing}`,
			answers.battery && `Battery backup: ${answers.battery}`,
		].filter(Boolean);
		const payload = {
			name: `${contact.firstName} ${contact.lastName}`.trim(),
			email: contact.email,
			phone: contact.phone,
			address: answers.zip || '',
			heardFrom: 'Website quiz',
			message:
				lines.length > 0
					? `Solar quiz responses:\n${lines.join('\n')}`
					: 'Submitted the solar quiz.',
		};

		try {
			await client.post('/message', payload);
			if (billFile) {
				try {
					const fd = new FormData();
					fd.append('bill', billFile);
					fd.append('email', contact.email);
					await client.post('/upload/contact', fd, {
						headers: { 'Content-Type': 'multipart/form-data' },
					});
				} catch {
					setAlert('warning', 'Submitted, but the bill upload failed.');
				}
			}
			setDone(true);
		} catch (err) {
			setAlert('error', err.message || 'Something went wrong.');
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<div className='bg-surface text-ink-900 antialiased'>
			<div className='mx-auto flex min-h-[80vh] max-w-2xl flex-col justify-center px-6 py-16 lg:px-8'>
				{/* Mode toggle — pick the guided quiz or a plain message form. Hidden
				    once a submission is done. */}
				{!done && (
					<div className='mx-auto mb-8 inline-flex rounded-full border-2 border-ink-200 p-1'>
						{[
							{ id: 'guided', label: 'Guided quote' },
							{ id: 'message', label: 'Send a message' },
						].map((opt) => (
							<button
								key={opt.id}
								onClick={() => setMode(opt.id)}
								className={`rounded-full px-5 py-2 text-sm font-semibold transition-colors ${
									mode === opt.id
										? 'bg-primary-500 text-white'
										: 'text-ink-600 hover:text-ink-900'
								}`}
							>
								{opt.label}
							</button>
						))}
					</div>
				)}

				<div className='w-full sg-card bg-white p-8 sm:p-12'>
					{done ? (
						<Done firstName={contact.firstName} />
					) : mode === 'message' ? (
						<SimpleForm
							onDone={() => setDone(true)}
							setAlert={setAlert}
						/>
					) : (
						<>
							{/* Progress bar */}
							<div className='mx-auto mb-12 h-2 w-full max-w-sm overflow-hidden rounded-full bg-ink-100'>
								<div
									className='h-full rounded-full bg-ink-900 transition-all duration-300'
									style={{ width: `${progress}%` }}
								/>
							</div>

							<h1 className='text-center font-display text-3xl font-medium tracking-tight text-ink-900 sm:text-4xl'>
								{current.title}
							</h1>
							{current.subtitle && (
								<p className='mt-3 text-center text-base text-ink-500'>
									{current.subtitle}
									{current.type === 'zip' && (
										<span className='text-rose-500'> *</span>
									)}
								</p>
							)}

							<div className='mt-10'>
								{current.type === 'zip' && (
									<ZipStep
										value={answers.zip || ''}
										onChange={(v) => setAnswer('zip', v)}
										onEnter={() => canContinue() && next()}
									/>
								)}

								{current.type === 'choice' && (
									<ChoiceGrid
										options={current.options}
										columns={current.columns}
										selected={answers[current.key]}
										onSelect={(v) => setAnswer(current.key, v)}
									/>
								)}

								{current.type === 'contact' && (
									<ContactStep
										contact={contact}
										setContact={setContact}
										billFile={billFile}
										setBillFile={setBillFile}
									/>
								)}
							</div>

							{/* Nav */}
							<div className='mt-10 flex items-center justify-center gap-3'>
								{step > 0 && (
									<button
										onClick={back}
										className='inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold text-ink-700 ring-1 ring-ink-200 hover:bg-surface transition-colors'
									>
										<ChevronLeftIcon className='h-4 w-4' /> Back
									</button>
								)}

								{current.type === 'contact' ? (
									<button
										onClick={submit}
										disabled={submitting}
										className='inline-flex items-center gap-2 rounded-full bg-primary-500 px-8 py-3 text-sm font-semibold text-white shadow-lg shadow-primary-500/20 hover:bg-primary-600 disabled:opacity-60 transition-all'
									>
										{submitting ? 'Submitting…' : 'Submit'}
									</button>
								) : (
									<button
										onClick={next}
										disabled={!canContinue()}
										className='inline-flex items-center gap-2 rounded-full bg-primary-500 px-8 py-3 text-sm font-semibold text-white shadow-lg shadow-primary-500/20 hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-40 transition-all'
									>
										Continue <ChevronRightIcon className='h-4 w-4' />
									</button>
								)}
							</div>

							{/* Skip — only on optional, non-contact steps */}
							{!current.required && current.type !== 'contact' && (
								<div className='mt-4 text-center'>
									<button
										onClick={skip}
										className='text-sm font-medium text-ink-400 hover:text-ink-600'
									>
										Skip this question
									</button>
								</div>
							)}
						</>
					)}
				</div>
			</div>
		</div>
	);
}

// Plain contact form — for people who'd rather just send a message than walk
// the guided quiz. Posts the same /message payload, with a honeypot and an
// optional utility-bill upload.
function SimpleForm({ onDone, setAlert }) {
	const [form, setForm] = useState({
		name: '',
		email: '',
		phone: '',
		message: '',
		website: '', // honeypot — must stay empty
	});
	const [billFile, setBillFile] = useState(null);
	const [submitting, setSubmitting] = useState(false);

	const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

	const fieldCls =
		'block w-full rounded-2xl border-0 px-4 py-3 text-base text-ink-900 ring-1 ring-inset ring-ink-300 placeholder:text-ink-300 focus:ring-2 focus:ring-inset focus:ring-primary-500';
	const labelCls = 'mb-1.5 block text-sm font-medium text-ink-600';

	const submit = async (e) => {
		e.preventDefault();
		// Honeypot tripped: pretend success, send nothing.
		if (form.website) return onDone();
		if (!form.name || !form.email || !form.phone || !form.message) {
			return setAlert(
				'error',
				'Please fill out name, email, phone, and message.',
			);
		}
		setSubmitting(true);
		try {
			await client.post('/message', {
				name: form.name,
				email: form.email,
				phone: form.phone,
				heardFrom: 'Contact form',
				message: form.message,
			});
			if (billFile) {
				try {
					const fd = new FormData();
					fd.append('bill', billFile);
					fd.append('email', form.email);
					await client.post('/upload/contact', fd, {
						headers: { 'Content-Type': 'multipart/form-data' },
					});
				} catch {
					setAlert('warning', 'Message sent, but the bill upload failed.');
				}
			}
			onDone();
		} catch (err) {
			setAlert('error', err.message || 'Something went wrong.');
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<form
			onSubmit={submit}
			className='mx-auto max-w-lg'
		>
			<h1 className='text-center font-display text-3xl font-medium tracking-tight text-ink-900 sm:text-4xl'>
				Send me a message
			</h1>
			<p className='mt-3 text-center text-base text-ink-500'>
				A few details and I&apos;ll get back to you within a day.
			</p>

			<div className='mt-10 space-y-5'>
				<div>
					<label className={labelCls}>
						Name <span className='text-rose-500'>*</span>
					</label>
					<input
						value={form.name}
						onChange={set('name')}
						className={fieldCls}
						autoComplete='name'
					/>
				</div>
				<div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
					<div>
						<label className={labelCls}>
							Email <span className='text-rose-500'>*</span>
						</label>
						<input
							type='email'
							value={form.email}
							onChange={set('email')}
							className={fieldCls}
							autoComplete='email'
						/>
					</div>
					<div>
						<label className={labelCls}>
							Phone <span className='text-rose-500'>*</span>
						</label>
						<input
							type='tel'
							value={form.phone}
							onChange={set('phone')}
							className={fieldCls}
							autoComplete='tel'
						/>
					</div>
				</div>
				<div>
					<label className={labelCls}>
						Message <span className='text-rose-500'>*</span>
					</label>
					<textarea
						rows={4}
						value={form.message}
						onChange={set('message')}
						className={fieldCls}
						placeholder='What can I help you with?'
					/>
				</div>
				<div>
					<label className={labelCls}>
						Utility bill{' '}
						<span className='font-normal text-ink-400'>(optional)</span>
					</label>
					<BillDropzone
						file={billFile}
						onFile={setBillFile}
					/>
				</div>

				{/* Honeypot — visually hidden, off-screen; bots fill it, humans don't. */}
				<div
					aria-hidden='true'
					style={{
						position: 'absolute',
						left: '-10000px',
						width: '1px',
						height: '1px',
						overflow: 'hidden',
					}}
				>
					<label htmlFor='website'>Website</label>
					<input
						id='website'
						name='website'
						type='text'
						tabIndex={-1}
						autoComplete='off'
						value={form.website}
						onChange={set('website')}
					/>
				</div>
			</div>

			<div className='mt-10 flex justify-center'>
				<button
					type='submit'
					disabled={submitting}
					className='inline-flex items-center gap-2 rounded-full bg-primary-500 px-8 py-3 text-sm font-semibold text-white shadow-lg shadow-primary-500/20 hover:bg-primary-600 disabled:opacity-60 transition-all'
				>
					{submitting ? 'Sending…' : 'Send message'}
				</button>
			</div>

			<p className='mt-5 text-center text-xs leading-5 text-ink-400'>
				By submitting, you agree to be contacted about your inquiry. We&apos;ll
				never sell or share your information.
			</p>
		</form>
	);
}

function ZipStep({ value, onChange, onEnter }) {
	return (
		<div className='mx-auto max-w-xs'>
			<input
				autoFocus
				inputMode='numeric'
				maxLength={5}
				value={value}
				onChange={(e) =>
					onChange(e.target.value.replace(/\D/g, '').slice(0, 5))
				}
				onKeyDown={(e) => e.key === 'Enter' && onEnter()}
				placeholder='21208'
				className='block w-full rounded-2xl border-0 px-5 py-4 text-center text-2xl tracking-wide text-ink-900 ring-2 ring-inset ring-ink-300 placeholder:text-ink-300 focus:ring-2 focus:ring-inset focus:ring-primary-500'
			/>
		</div>
	);
}

function ChoiceGrid({ options, columns = 2, selected, onSelect }) {
	const colCls = columns === 3 ? 'sm:grid-cols-3' : 'sm:grid-cols-2';
	return (
		<div className={`mx-auto grid max-w-lg grid-cols-1 gap-4 ${colCls}`}>
			{options.map((opt) => {
				const isSel = selected === opt.value;
				const Icon = opt.icon;
				return (
					<button
						key={opt.value}
						onClick={() => onSelect(opt.value)}
						className={`flex flex-col items-center justify-center gap-3 rounded-lg border-2 px-4 py-6 text-center transition-colors ${
							isSel
								? 'border-primary-500 bg-primary-50'
								: 'border-ink-200 bg-transparent hover:border-primary-400'
						}`}
					>
						{Icon && <Icon className='h-9 w-9 text-primary-600' />}
						<span className='font-display text-lg font-medium text-ink-900'>
							{opt.value}
						</span>
						{opt.sub && <span className='text-sm text-ink-500'>{opt.sub}</span>}
					</button>
				);
			})}
		</div>
	);
}

function ContactStep({ contact, setContact, billFile, setBillFile }) {
	const set = (k) => (e) => setContact((c) => ({ ...c, [k]: e.target.value }));
	const fieldCls =
		'block w-full rounded-2xl border-0 px-4 py-3 text-base text-ink-900 ring-1 ring-inset ring-ink-300 placeholder:text-ink-300 focus:ring-2 focus:ring-inset focus:ring-primary-500';
	const labelCls = 'mb-1.5 block text-sm font-medium text-ink-600';
	return (
		<div className='mx-auto max-w-lg space-y-5'>
			<div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
				<div>
					<label className={labelCls}>
						First Name <span className='text-rose-500'>*</span>
					</label>
					<input
						value={contact.firstName}
						onChange={set('firstName')}
						className={fieldCls}
						autoComplete='given-name'
					/>
				</div>
				<div>
					<label className={labelCls}>
						Last Name <span className='text-rose-500'>*</span>
					</label>
					<input
						value={contact.lastName}
						onChange={set('lastName')}
						className={fieldCls}
						autoComplete='family-name'
					/>
				</div>
			</div>
			<div>
				<label className={labelCls}>
					Email Address <span className='text-rose-500'>*</span>
				</label>
				<input
					type='email'
					value={contact.email}
					onChange={set('email')}
					className={fieldCls}
					autoComplete='email'
				/>
			</div>
			<div>
				<label className={labelCls}>
					Phone Number <span className='text-rose-500'>*</span>
				</label>
				<input
					type='tel'
					value={contact.phone}
					onChange={set('phone')}
					className={fieldCls}
					autoComplete='tel'
				/>
			</div>
			<div>
				<label className={labelCls}>
					Utility bill{' '}
					<span className='font-normal text-ink-400'>(optional)</span>
				</label>
				<BillDropzone
					file={billFile}
					onFile={setBillFile}
				/>
			</div>
			<p className='text-xs leading-5 text-ink-400'>
				By submitting, you agree to be contacted about your inquiry. We&apos;ll
				never sell or share your information.
			</p>
		</div>
	);
}

function Done({ firstName }) {
	return (
		<div className='py-10 text-center'>
			<div className='mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-primary-100'>
				<CheckIcon className='h-9 w-9 text-primary-600' />
			</div>
			<h1 className='mt-6 font-display text-3xl font-medium text-ink-900'>
				Thanks{firstName ? `, ${firstName}` : ''}!
			</h1>
			<p className='mx-auto mt-3 max-w-md text-base leading-7 text-ink-500'>
				We&apos;ve got your information and we&apos;ll reach out shortly with a
				clear picture of your solar savings.
			</p>
		</div>
	);
}
